// Doplňky karty produktu z INDEXU (`/specs`): konec akční ceny v cenové
// pilulce („do 5. 9." / „končí za 13:42", saleEnd.js) a samolepka Bestseller
// (productCard.js `mountSticker`). Jede nad každou kartou, kterou dekoruje
// productCard.js: výpis kategorie/značky, řady homepage, související produkty,
// upsell, výsledky hledání.
//
// Zdroje:
//   • karta z indexu (výsledky hledání, productCardRender.js) nese
//     `data-bs-sale-to` / `data-bs-bestseller` rovnou — žádný dotaz. Poznávací
//     znamení je `data-bs-code`: index o ní ví všechno, chybějící atribut
//     znamená „není", ne „nevím";
//   • nativní karta Shoptetu má jen `data-micro="sku"` → dávkový GET `/specs`
//     přes specsCache.js (tentýž endpoint a cache jako detail, sticky lišta
//     i modal). Karty výpisu kategorie obsluhuje specChips.js ze své odpovědi
//     (`mountSaleEnd` + `mountSticker`) — jeden GET na stránku.
//
// Guard konce akce „stránka reálně nese slevu" = nativní `.price-standard`/
// `.price-save` v kartě. Index jede za platformou (delta 15 min), cena na
// kartě je pravda. Bestseller guard nemá — je to výpočet z tržeb za 90 dní,
// který platforma nikde nenese (nativní TIP visí na 85 % katalogu a nekreslí se).
//
// Velkoobchod (ceník ≠ 1, 2026-09-14): Bestseller ano (`getIndexConfig`), konec
// akce NE — `action` v indexu je akční cena ceníku 1 a VO ceník akce nemá.
//
// Předpokládané naskladnění (restockDate.js, přepínač `restockDate`) ze STEJNÉ
// odpovědi: nativní karta ho dostane v `applyItem`, karta z indexu (výsledky
// hledání, doporučení) jinak `/specs` nepotřebuje — vyprodaná se proto přidá do
// téhož dávkového GETu, jen pro termín.

import { getIndexConfig, getRetailConfig } from './searchConfig.js';
import { createSaleEnd } from './saleEnd.js';
import { mountSticker } from './productCard.js';
import { specsFor } from './specsCache.js';
import { mountCardRestock, wantsCardRestock } from './restockDate.js';

const CARD_SELECTOR = '.p[data-micro="product"]';
const SEGMENT_CLASS = 'bs-card__sale';

function codeOf(card) {
  const el = card.querySelector('[data-micro="sku"]');
  return el ? (el.textContent || '').trim() : '';
}

/** Karta nese slevu (přeškrtnutá cena nebo procenta) — bez toho se konec akce nekreslí. */
export function hasDiscount(card) {
  return !!card.querySelector('.price-standard, .price-save');
}

/** Cíl segmentu: přesunutá pilulka (`.bs-card__pill`), u mini karty `.prices` v toku. */
function pillOf(card) {
  return card.querySelector('.bs-card__pill') || card.querySelector('.prices');
}

/**
 * Připne segment konce akce do pilulky karty. Volá to i specChips.js pro karty
 * výpisu kategorie — ty dostanou konec akce ze STEJNÉ odpovědi `/specs` jako
 * chipy (jeden GET na stránku), tenhle modul je proto přeskakuje.
 */
export function mountSaleEnd(card, toDate) {
  // akce v indexu je ceníku 1 — velkoobchodu se konec nekreslí (getRetailConfig)
  if (!toDate || !getRetailConfig() || !hasDiscount(card)) return;
  const pill = pillOf(card);
  if (!pill || pill.querySelector('.' + SEGMENT_CLASS)) return;
  const el = createSaleEnd(toDate, { className: SEGMENT_CLASS, variant: 'card' });
  if (el) pill.appendChild(el);
}

/** Doplňky z jedné položky `/specs` na jednu kartu. */
export function applyItem(card, item) {
  if (!item) return;
  if (item.action && item.action.to) mountSaleEnd(card, item.action.to);
  if (item.bestseller === true) mountSticker(card);
  mountCardRestock(card, item);
}

function pendingCards(root) {
  const out = [];
  const cards = (root || document).querySelectorAll(CARD_SELECTOR);
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (card.dataset.bsExtras) continue;
    // productCard.js kartu ještě nedekoroval → pilulka neexistuje, přijde znovu
    if (!card.dataset.bsCard) continue;
    card.dataset.bsExtras = '1';
    // kartu výpisu kategorie obsluhuje specChips.js (sdílený dotaz), viz mountSaleEnd
    if (card.dataset.bsSpecs) continue;
    out.push(card);
  }
  return out;
}

async function run(root) {
  const cards = pendingCards(root);
  if (!cards.length) return;

  const byCode = new Map();
  const restockByCode = new Map();
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (card.dataset.bsSaleTo) mountSaleEnd(card, card.dataset.bsSaleTo);
    // karta z indexu: bestseller už nakreslil productCard.js z atributu, z `/specs`
    // chybí jen termín naskladnění u vyprodané
    if (card.dataset.bsCode) {
      if (wantsCardRestock(card)) {
        const code = card.dataset.bsCode;
        if (!restockByCode.has(code)) restockByCode.set(code, []);
        restockByCode.get(code).push(card);
      }
      continue;
    }
    const code = codeOf(card);
    if (!code) continue;
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(card);
  }
  if (!byCode.size && !restockByCode.size) return;

  const codes = [...byCode.keys()];
  restockByCode.forEach(function (_, code) {
    if (!byCode.has(code)) codes.push(code);
  });
  const items = await specsFor(codes);
  items.forEach(function (item, code) {
    if (!item) return;
    const targets = byCode.get(code) || [];
    for (let t = 0; t < targets.length; t++) applyItem(targets[t], item);
    const restock = restockByCode.get(code) || [];
    for (let t = 0; t < restock.length; t++) mountCardRestock(restock[t], item);
  });
}

let scheduled = false;
function schedule(root) {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(function () {
    scheduled = false;
    run(root || document);
  });
}

function touchesCard(node) {
  return node.nodeType === 1 && (node.matches(CARD_SELECTOR) || !!node.querySelector(CARD_SELECTOR));
}

/**
 * Doplňky z indexu na kartách. Idempotentní (re-init po ShoptetDOMPageContentLoaded);
 * karty dosypané AJAXem (loadMore, hledání, upsell v modalu) chytá observer.
 * Bez konfigurace indexu (Ú0) se nespustí vůbec; konec akce hlídá `mountSaleEnd`.
 */
export function initCardExtras() {
  if (!getIndexConfig()) return;
  run(document);

  if (document.body.dataset.bsExtrasObserved) return;
  document.body.dataset.bsExtrasObserved = '1';
  new MutationObserver(function (muts) {
    for (let i = 0; i < muts.length; i++) {
      const added = muts[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        if (touchesCard(added[j])) {
          schedule(document);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}
